import { useState } from "react";
import type {
  ActivityField,
  ActivityLog,
  ActivityType,
  FieldValue,
  Membership,
} from "@lifelog/shared";
import { coerceValues } from "@lifelog/shared";
import { api, ApiError } from "../lib/api.js";
import { ModalShell } from "./ModalShell.js";
import { TimeSelector } from "./TimeSelector.js";
import { ActivityIcon } from "./icons.js";
import { PhotoField } from "./PhotoField.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

function dollarsFromMinor(minor: number): string {
  return (minor / 100).toFixed(2);
}

function minorFromDollars(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

export function ActivityFieldInputs({
  fields,
  values,
  errors,
  onChange,
}: {
  fields: ActivityField[];
  values: Record<string, FieldValue>;
  errors: Record<string, string>;
  onChange: (fieldId: string, value: FieldValue | undefined) => void;
}) {
  const ordered = [...fields].sort((a, b) => a.order - b.order);
  return (
    <div className="space-y-3">
      {ordered.map((field) => (
        <label key={field.fieldId} className="block">
          <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            {field.label}
            {field.required ? "" : " (optional)"}
          </span>
          <FieldControl field={field} value={values[field.fieldId]} onChange={onChange} />
          {errors[field.fieldId] && (
            <span className="mt-1 block text-xs text-red-600 dark:text-red-400">
              {errors[field.fieldId]}
            </span>
          )}
        </label>
      ))}
    </div>
  );
}

function FieldControl({
  field,
  value,
  onChange,
}: {
  field: ActivityField;
  value: FieldValue | undefined;
  onChange: (fieldId: string, value: FieldValue | undefined) => void;
}) {
  switch (field.kind) {
    case "text":
      return (
        <input
          type="text"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(field.fieldId, e.target.value || undefined)}
          className={inputCls}
        />
      );
    case "number":
      return <NumberInput field={field} value={value} onChange={onChange} />;
    case "duration":
      return (
        <div className="relative">
          <input
            type="text"
            inputMode="numeric"
            value={value === undefined ? "" : String(value)}
            onChange={(e) => {
              const raw = e.target.value.trim();
              if (!raw) {
                onChange(field.fieldId, undefined);
                return;
              }
              const n = Number(raw);
              onChange(field.fieldId, Number.isInteger(n) ? n : undefined);
            }}
            className={`${inputCls} pr-12`}
          />
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">
            {field.unit ?? "min"}
          </span>
        </div>
      );
    case "currency":
      return (
        <CurrencyInput field={field} value={value} onChange={onChange} />
      );
    case "boolean":
      return (
        <button
          type="button"
          role="switch"
          aria-checked={value === true}
          onClick={() => onChange(field.fieldId, value !== true)}
          className={
            "flex h-11 w-14 items-center rounded-full px-1 transition-colors " +
            (value === true ? "bg-(--accent)" : "bg-gray-200 dark:bg-gray-700")
          }
        >
          <span
            className={
              "h-8 w-8 rounded-full bg-white shadow transition-transform " +
              (value === true ? "translate-x-3" : "")
            }
          />
        </button>
      );
    case "enum": {
      const options = field.options ?? [];
      if (options.length <= 4) {
        return (
          <div className="flex flex-wrap gap-1.5" role="group">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(field.fieldId, opt)}
                className={
                  "min-h-11 rounded-full px-3 text-sm " +
                  (value === opt
                    ? "bg-(--accent) text-(--accent-contrast)"
                    : "border border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-300")
                }
              >
                {opt}
              </button>
            ))}
          </div>
        );
      }
      return (
        <select
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(field.fieldId, e.target.value || undefined)}
          className={inputCls}
        >
          <option value="">Choose…</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    default:
      return null;
  }
}

function NumberInput({
  field,
  value,
  onChange,
}: {
  field: ActivityField;
  value: FieldValue | undefined;
  onChange: (fieldId: string, value: FieldValue | undefined) => void;
}) {
  const [text, setText] = useState(() => (value === undefined ? "" : String(value)));
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const raw = e.target.value.trim();
          if (!raw) {
            onChange(field.fieldId, undefined);
            return;
          }
          const n = Number(raw);
          onChange(field.fieldId, Number.isFinite(n) ? n : undefined);
        }}
        className={`${inputCls} ${field.unit ? "pr-12" : ""}`}
      />
      {field.unit && (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">
          {field.unit}
        </span>
      )}
    </div>
  );
}

function CurrencyInput({
  field,
  value,
  onChange,
}: {
  field: ActivityField;
  value: FieldValue | undefined;
  onChange: (fieldId: string, value: FieldValue | undefined) => void;
}) {
  const [text, setText] = useState(() =>
    typeof value === "number" ? dollarsFromMinor(value) : "",
  );
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-400">
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const minor = minorFromDollars(e.target.value);
          onChange(field.fieldId, minor);
        }}
        className={`${inputCls} pl-7 pr-12`}
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">
        {field.unit ?? "USD"}
      </span>
    </div>
  );
}

export function PerformedBySelect({
  members,
  currentUserId,
  value,
  onChange,
}: {
  members: Membership[];
  currentUserId: string;
  value: string | undefined;
  onChange: (userId: string | undefined) => void;
}) {
  if (members.length < 2) return null;
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
        Performed by
      </span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        className={inputCls}
      >
        <option value="">Not specified</option>
        {members.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.userId === currentUserId ? `${m.displayName} (me)` : m.displayName}
          </option>
        ))}
      </select>
    </label>
  );
}

export function QuickLogModal({
  type,
  members,
  currentUserId,
  defaultPerformedBySelf,
  source = "quick_button",
  onClose,
  onSaved,
}: {
  type: ActivityType;
  members: Membership[];
  currentUserId: string;
  defaultPerformedBySelf: boolean;
  source?: ActivityLog["source"];
  onClose: () => void;
  onSaved: (log: ActivityLog) => void;
}) {
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [occurredAt, setOccurredAt] = useState<string | null>(null);
  const [performedByUserId, setPerformedByUserId] = useState<string | undefined>(
    defaultPerformedBySelf ? currentUserId : undefined,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoKey, setPhotoKey] = useState<string | undefined>();
  const [photoLogId, setPhotoLogId] = useState<string | undefined>();

  function setField(fieldId: string, value: FieldValue | undefined) {
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[fieldId];
      else next[fieldId] = value;
      return next;
    });
    setErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }

  async function save() {
    const coerced = coerceValues(type.fields, values);
    if (!coerced.ok) {
      setErrors(coerced.errors);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const log = await api<ActivityLog>("/logs", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        json: {
          activityTypeId: type.activityTypeId,
          occurredAt: occurredAt ?? undefined,
          values: coerced.values,
          note: note.trim() || undefined,
          performedByUserId,
          source,
          photoKey,
          logId: photoLogId,
        },
      });
      onSaved(log);
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors) {
        setErrors(err.fieldErrors);
      } else {
        setError(err instanceof Error ? err.message : "Couldn't save.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title={type.name}
      subtitle="Log this activity"
      icon={<ActivityIcon name={type.iconName} size={18} className="text-(--accent)" />}
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-11 flex-1 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="min-h-11 flex-1 rounded-lg bg-(--accent) text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </p>
        )}
        <ActivityFieldInputs
          key={type.activityTypeId}
          fields={type.fields}
          values={values}
          errors={errors}
          onChange={setField}
        />
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Note (optional)
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className={`${inputCls} resize-none`}
          />
        </label>
        <div>
          <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">When</span>
          <TimeSelector value={occurredAt} onChange={setOccurredAt} />
        </div>
        <PerformedBySelect
          members={members}
          currentUserId={currentUserId}
          value={performedByUserId}
          onChange={setPerformedByUserId}
        />
        <PhotoField
          activityTypeId={type.activityTypeId}
          photoKey={photoKey}
          disabled={busy}
          onChange={(next) => {
            setPhotoKey(next?.photoKey);
            setPhotoLogId(next?.logId);
          }}
        />
      </div>
    </ModalShell>
  );
}
